import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import type { TimetableGridEntry } from './typesV2'
import { DAY_LABELS } from './typesV2'

interface EntryEditPopupProps {
  entries?: TimetableGridEntry[]
  entryIndices?: number[]
  newEntry?: TimetableGridEntry | null
  mode?: 'import' | 'dashboard'
  onSave?: (index: number, updatedEntry: TimetableGridEntry) => void | Promise<void>
  onSaveNew?: (entry: TimetableGridEntry) => void | Promise<void>
  onDelete?: (index: number) => void | Promise<void>
  onClose: () => void
}

export function EntryEditPopup({ entries, entryIndices, newEntry, mode = 'import', onSave, onSaveNew, onDelete, onClose }: EntryEditPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null)
  const [saving, setSaving] = useState(false)
  const isNew = newEntry !== undefined && newEntry !== null
  const [localEntries, setLocalEntries] = useState<TimetableGridEntry[]>(() =>
    isNew ? [{ ...(newEntry!), lesson_count: 1, is_multi_period: false }] : (entryIndices?.map(idx => ({ ...entries![idx]!, lesson_count: entries![idx]!.lesson_count || 1, is_multi_period: entries![idx]!.is_multi_period || false })) || [])
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    const firstInput = popupRef.current?.querySelector('input, select') as HTMLElement | null
    if (firstInput) {
      firstInput.focus()
    }
  }, [])

  useEffect(() => {
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusableElements = popupRef.current?.querySelectorAll(
        'input, select, button, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusableElements || focusableElements.length === 0) return

      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [])

  const handleFieldChange = (entryIdx: number, field: keyof TimetableGridEntry, value: string | number | boolean | null) => {
    setLocalEntries(prev => {
      const updated = [...prev]
      updated[entryIdx] = { ...updated[entryIdx]!, [field]: value }
      return updated
    })
  }

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    try {
      if (isNew && onSaveNew) {
        await onSaveNew(localEntries[0]!)
      } else if (onSave && entryIndices) {
        for (let i = 0; i < localEntries.length; i++) {
          await onSave(entryIndices[i]!, localEntries[i]!)
        }
      }
      onClose()
    } catch {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (saving) return
    setSaving(true)
    try {
      if (onDelete && entryIndices) {
        for (const idx of entryIndices) {
          await onDelete(idx)
        }
      }
      onClose()
    } catch {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={() => { if (!saving) onClose() }}
    >
      <div
        ref={popupRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="entry-edit-title"
      >
        <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
          <h3 id="entry-edit-title" className="font-semibold text-sm text-gray-900">
            {isNew ? 'Neuen Eintrag erstellen' : (entryIndices!.length > 1 ? `Einträge bearbeiten (${entryIndices!.length})` : 'Eintrag bearbeiten')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {localEntries.map((entry, i) => (
            <div key={i} className="space-y-3">
              {entryIndices && entryIndices.length > 1 && (
                <div className="text-xs text-gray-500 font-medium mb-2 pb-2 border-b">
                  Eintrag #{entryIndices[i]! + 1}
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 font-medium">Wochentag</label>
                <select
                  value={entry.day_of_week || ''}
                  onChange={e => handleFieldChange(i, 'day_of_week', e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">—</option>
                  {Object.entries(DAY_LABELS).filter(([k]) => !k.startsWith('__')).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Zeit</label>
                <input
                  value={entry.time_slot_label || ''}
                  onChange={e => handleFieldChange(i, 'time_slot_label', e.target.value)}
                  placeholder="z.B. 7:45-8:30"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div className={mode === 'dashboard' ? '' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
                {mode !== 'dashboard' && (
                <div>
                  <label className="text-xs text-gray-500 font-medium">Lehrer-Code</label>
                  <input
                    value={entry.teacher_code || ''}
                    onChange={e => handleFieldChange(i, 'teacher_code', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 font-medium">Fach</label>
                  <input
                    value={entry.subject || ''}
                    onChange={e => handleFieldChange(i, 'subject', e.target.value)}
                    disabled={mode === 'dashboard' && !isNew}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  {mode === 'dashboard' && !isNew ? (
                    <p className="mt-1 text-xs text-gray-500">Fach oder Kategorie umhängen: Zelle einmal anklicken</p>
                  ) : null}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Raum</label>
                  <input
                    value={entry.room || ''}
                    onChange={e => handleFieldChange(i, 'room', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Klasse</label>
                  <input
                    value={entry.class_name || ''}
                    onChange={e => handleFieldChange(i, 'class_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Lektion</label>
                <input
                  type="number"
                  value={entry.lesson_number ?? ''}
                  onChange={e => handleFieldChange(i, 'lesson_number', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              {mode === 'dashboard' && (
                <div>
                  <label className="text-xs text-gray-500 font-medium">Datum (Einzeltermin)</label>
                  <input
                    type="date"
                    value={entry.date || ''}
                    onChange={e => handleFieldChange(i, 'date', e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              )}
              {mode === 'import' && (
                <div>
                  <label className="text-xs text-gray-500 font-medium">Multi-Period</label>
                  <select
                    value={entry.is_multi_period ? 'true' : 'false'}
                    onChange={e => handleFieldChange(i, 'is_multi_period', e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="false">Nein</option>
                    <option value="true">Ja</option>
                  </select>
                </div>
              )}
              {mode === 'import' && localEntries[i]?.is_multi_period && (
                <div>
                  <label className="text-xs text-gray-500 font-medium">Lektionen-Anzahl</label>
                  <input
                    type="number"
                    min="1"
                    value={entry.lesson_count || 1}
                    onChange={e => handleFieldChange(i, 'lesson_count', e.target.value ? parseInt(e.target.value) : 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50 rounded-b-lg shrink-0">
          {!isNew && onDelete && (
            <button onClick={handleDelete} disabled={saving} className="px-3 py-2 text-red-600 text-sm font-medium hover:bg-red-50 rounded-md transition-colors disabled:opacity-50">
              Löschen
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} disabled={saving} className="px-4 py-2 text-gray-700 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50">
              Abbrechen
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
